<template>
  <div class="fixed inset-0 flex h-full w-full flex-col items-center justify-between bg-black/[.90] backdrop-blur-[8px]" @click.self="onClickClose">
    <div class="relative flex w-full items-center justify-center p-4 md:p-6">
      <div class="text-white">{{ selectedIndex + 1 }} / {{ imageCount }}</div>
      <div class="absolute right-10 ml-auto h-3 w-3 cursor-pointer md:h-4 md:w-4" @click="onClickClose">
        <img src="@/assets/icon/close/white.svg" />
      </div>
    </div>
    <div class="w-full md:max-w-[1216px] md:px-2">
      <Carousel ref="mainCarouselRef" :opts="{ startIndex: props.initialIndex }" @click="handleMainCarouselClick" @init-api="(val) => (emblaMainApi = val)">
        <CarouselContent class="m-0">
          <CarouselItem v-for="(imageUrl, index) in imgUrls" :key="index" class="flex justify-center">
            <OptimizedImage loading="lazy" :alt="`experience-image-${index}`" :class="['aspect-[3/2] object-cover md:h-[80svh]']" :image-url="imageUrl" :optimized-image-options="{ width: 1280 }" />
          </CarouselItem>
        </CarouselContent>
        <CarouselPreviousBase v-if="!isSP" :can-scroll-prev="canScrollPrev" :on-click-scroll-prev="onClickScrollPrev" :orientation="'horizontal'" :should-display-by-default="true" />
        <CarouselNextBase v-if="!isSP" :can-scroll-next="canScrollNext" :on-click-scroll-next="onClickScrollNext" :orientation="'horizontal'" :should-display-by-default="true" />
        <CarouselDotIndicator :should-display-by-default="true" />
      </Carousel>
    </div>
    <div class="pt-2 md:px-2 md:pb-2">
      <Carousel :opts="{ startIndex: props.initialIndex }" @init-api="(val) => (emblaThumbnailApi = val)">
        <CarouselContent>
          <CarouselItem v-for="(imageUrl, index) in imgUrls" :key="index" class="basis-auto cursor-pointer pl-0" @click="onThumbClick(index)">
            <OptimizedImage
              loading="lazy"
              :alt="`experience-image-${index}`"
              :class="['aspect-[3/2] w-[80px] object-cover object-center md:w-[120px]', selectedIndex === index ? 'opacity-30' : '']"
              :image-url="imageUrl"
              :optimized-image-options="{ width: 240 }"
            />
          </CarouselItem>
        </CarouselContent>
      </Carousel>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useElementSize, watchOnce } from '@vueuse/core';

import type { CarouselApi } from '@/components/lib/shadcn-vue/carousel';
import Carousel from '@/components/lib/shadcn-vue/carousel/Carousel.vue';
import CarouselContent from '@/components/lib/shadcn-vue/carousel/CarouselContent.vue';
import CarouselDotIndicator from '@/components/lib/shadcn-vue/carousel/CarouselDotIndicator.vue';
import CarouselItem from '@/components/lib/shadcn-vue/carousel/CarouselItem.vue';
import CarouselNextBase from '@/components/lib/shadcn-vue/carousel/CarouselNextBase.vue';
import CarouselPreviousBase from '@/components/lib/shadcn-vue/carousel/CarouselPreviousBase.vue';
import { useIsSP } from '@/composables/ui/useIsSP';
import { OptimizedImage } from '@/wabunka-src/common/components';

import type { VNodeRef } from 'vue';

interface Props {
  imgUrls: string[];
  onClickClose: () => void;
  initialIndex?: number;
}
const props = withDefaults(defineProps<Props>(), {
  initialIndex: 0,
});

const mainCarouselRef = ref<VNodeRef | null>(null);

const { width: mainCarouselElementWidth } = useElementSize(mainCarouselRef);
const isSP = useIsSP();

const imageCount = computed<number>(() => {
  if (!props.imgUrls) {
    return 0;
  }

  return props.imgUrls.length;
});

const isCarouselScrolling = ref<boolean>(false);

const emblaMainApi = ref<CarouselApi>();
const emblaThumbnailApi = ref<CarouselApi>();
const selectedIndex = ref<number>(props.initialIndex);

const handleMainCarouselClick = (e: MouseEvent) => {
  if (isCarouselScrolling.value || !emblaMainApi.value) {
    return;
  }

  if (e.offsetX < mainCarouselElementWidth.value / 2) {
    emblaMainApi.value.scrollPrev();
    return;
  }

  emblaMainApi.value.scrollNext();
};

const canScrollPrev = computed<boolean>(() => {
  return selectedIndex.value !== 0;
});
const canScrollNext = computed<boolean>(() => {
  return selectedIndex.value + 1 < props.imgUrls.length;
});

const onClickScrollPrev = (e: MouseEvent) => {
  e.preventDefault();
  e.stopPropagation();

  return emblaMainApi.value?.scrollPrev();
};
const onClickScrollNext = (e: MouseEvent) => {
  e.preventDefault();
  e.stopPropagation();

  return emblaMainApi.value?.scrollNext();
};

const onSelect = () => {
  if (!emblaMainApi.value || !emblaThumbnailApi.value) {
    return;
  }

  selectedIndex.value = emblaMainApi.value.selectedScrollSnap();
  emblaThumbnailApi.value.scrollTo(emblaMainApi.value.selectedScrollSnap());
};

const onThumbClick = (index: number) => {
  if (!emblaMainApi.value || !emblaThumbnailApi.value) {
    return;
  }
  emblaMainApi.value.scrollTo(index);
};

watchOnce(emblaMainApi, (emblaMainApi) => {
  if (!emblaMainApi) {
    return;
  }
  onSelect();
  emblaMainApi.on('select', onSelect);
  emblaMainApi.on('reInit', onSelect);
});

onMounted(() => {
  if (!emblaMainApi.value) {
    return;
  }

  // Set scrolling status on select/settle event
  // ref: https://www.embla-carousel.com/api/events/#scroll
  // settle eventが発火されるまでの時間が長いので考えどころ
  emblaMainApi.value.on('select', () => {
    isCarouselScrolling.value = true;
  });
  emblaMainApi.value.on('settle', () => {
    isCarouselScrolling.value = false;
  });
});
</script>

<style lang="scss" scoped>
.image-modal {
  display: flex;
  overscroll-behavior: contain;
  overflow-y: scroll;
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  backdrop-filter: blur(8px);

  &__inner {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
    max-width: 800px;
  }
}

.main-image {
  width: 100%;
  max-width: 800px;
  position: relative;

  // 疑似要素で横幅からアスペクト比の高さを確保する
  &:before {
    content: '';
    display: block;
    padding-top: 66.66%;
    /* アスペクト比率 3:2 = 100:66.66 */
  }

  &__item {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
  }

  &__img {
    width: 100%;
  }

  &__btn-area {
    position: absolute;
    top: 0;
    height: 100%;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
}

.prev-btn-area {
  width: 40%;
  height: 80%;
  position: relative;
  cursor: pointer;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
}

.prev-btn {
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  position: absolute;
  top: 48%;

  // remove button style
  border-color: transparent;

  &:focus {
    outline: none;
  }

  height: 32px;
  width: 32px;

  @apply md:h-[46px] md:w-[46px];

  &__img {
    height: 20px;
    width: 10px;

    @apply md:h-[26px] md:w-[13px];
  }
}

.next-btn-area {
  width: 40%;
  height: 80%;
  position: relative;
  cursor: pointer;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
}

.next-btn {
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  position: absolute;
  top: 48%;
  right: 0;

  // remove button style
  border-color: transparent;

  &:focus {
    outline: none;
  }

  height: 32px;
  width: 32px;

  @apply md:h-[46px] md:w-[46px];

  &__img {
    height: 20px;
    width: 10px;
    z-index: 1;

    @apply md:h-[26px] md:w-[13px];
  }
}

.sub-image-container {
  width: 100%;

  // scroll設定
  overflow-x: scroll;

  &::-webkit-scrollbar {
    height: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: white;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }

  // firefox用
  scrollbar-color: transparent white;
}

.sub-image-list {
  display: flex;
  align-items: flex-start;
  flex-wrap: nowrap;
  padding-bottom: 16px;
}

.sub-image-item {
  position: relative;
  flex-shrink: 0;
  width: 80px;
  height: auto;

  &:nth-child(n - 1) {
    margin-right: 8px;
  }

  @apply md:w-[120px] md:[&:nth-child(n-1)]:mr-4;

  &__filter {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
  }
}

.image-above-area {
  position: absolute;
  top: -24px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;

  @apply md:top-[-32px];
}

.image-index {
  font-family: 'Noto Sans JP', sans-serif;
  color: white;
  font-size: 14px;
  line-height: 14px;
  letter-spacing: 0;
  margin-left: 16px;

  @apply md:ml-0;
}

.close-btn {
  cursor: pointer;
  margin-right: 16px;
  width: 12px;
  height: 12px;
  display: flex;
  justify-content: center;
  align-items: center;

  @apply md:mr-0 md:h-4 md:w-4;
}

.non-scroll {
  height: calc(100vh + 1px);
  width: 1px;
  background-color: transparent;
}
</style>
